import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip } from '../ui';
import {
    SIMILAR_BAND, SMALL_SAMPLE_MIN, benchmarkPhrase, benchmarkWho, chosenNamesTip, compareTakeaway, compareTitle, formatKpiValue,
    rangeEnds, selectionSubject,
} from '../../utils/branchCompare';
import { BenchLabel } from './bits';

// Verdict → chip tone (semantic tokens) and the word on the chip.
export const VERDICT = {
    better: { tone: 'ok', word: 'Better' },
    worse: { tone: 'crit', word: 'Worse' },
    similar: { tone: 'neutral', word: 'Similar' },
};

/** A verdict chip ("Better" / "Worse" / "Similar") and the plain difference ("3.0 pts higher"). */
export function CompareDelta({ delta, testId = 'cmp-delta' }) {
    if (!delta.verdict) {
        return <span className="br2-vd br2-ink-muted" data-testid={testId}>—</span>;
    }
    const v = VERDICT[delta.verdict];
    return (
        <span className="br2-vd" data-testid={testId} data-tone={delta.tone} data-verdict={delta.verdict}>
            <Chip tone={v.tone} className="br2-vd-chip">{v.word}</Chip>
            <span className="br2-vd-t">{delta.diffText}</span>
        </span>
    );
}

CompareDelta.propTypes = {
    delta: PropTypes.shape({ verdict: PropTypes.string, tone: PropTypes.string, diffText: PropTypes.string }).isRequired,
    testId: PropTypes.string,
};

/**
 * Comparison bar: the shaded track runs from the lowest to the highest branch,
 * the selection is a dot, the benchmark a tall contrasting tick. Zero-based only
 * when there is no branch range; it also fits
 * negative values (the asking vs AI gap). Neutral colour: the verdict chip
 * carries good / bad. When every branch has the same figure there is nothing
 * to place on a track, so a short note says so instead. `single`: one branch
 * is selected, so an end it sits on is marked "(this branch)".
 */
export function BulletBar({ sel, bench, lo = null, hi = null, kind = 'rate', single = false }) {
    if (sel == null && bench == null) return null;
    const ends = rangeEnds({ kind, lo, hi, sel, single });
    if (ends?.same) {
        return <span className="br2-bul-same" data-testid="cmp-range-same">{ends.text}</span>;
    }
    // Track = lowest to highest single branch, so the eye sees where the
    // selection sits in the field. Without a range (one branch, tests) it
    // falls back to zero-based.
    const ranged = lo != null && hi != null && hi > lo;
    const vals = ranged ? [lo, hi, sel ?? lo, bench ?? lo] : [0, sel ?? 0, bench ?? 0];
    const rawMin = Math.min(...vals);
    const rawMax = Math.max(...vals);
    const pad = ranged ? (rawMax - rawMin) * 0.06 : (rawMax - rawMin) * 0.1 || 1;
    const min = ranged ? rawMin - pad : (rawMin < 0 ? rawMin - pad : 0);
    const max = ranged ? rawMax + pad : (rawMax > 0 ? rawMax + pad : 0);
    const span = max - min || 1;
    const pos = (v) => ((v - min) / span) * 100;
    return (
        <span className="br2-bulw">
            <span className="br2-bul" aria-hidden="true">
                {ranged && <span className="br2-bul-rng" style={{ left: `${pos(lo)}%`, width: `${pos(hi) - pos(lo)}%` }} />}
                {!ranged && min < 0 && <span className="br2-bul-zero" style={{ left: `${pos(0)}%` }} />}
                {bench != null && <span className="br2-bul-tick" style={{ left: `${pos(bench)}%` }} />}
                {sel != null && <span className="br2-bul-dot" style={{ left: `${pos(sel)}%` }} />}
            </span>
            {ranged && (
                <span className="br2-bul-ends" data-testid="cmp-range">
                    <span>{ends.lo}</span>
                    <span>{ends.hi}</span>
                </span>
            )}
        </span>
    );
}

BulletBar.propTypes = {
    sel: PropTypes.number, bench: PropTypes.number, lo: PropTypes.number, hi: PropTypes.number, kind: PropTypes.string, single: PropTypes.bool,
};

const n = (v) => (v || 0).toLocaleString('en-US');

export const CATEGORY_COMPARE_FOLD = 10;

// Rows computed from the inventory units, which load after /combined.
const INVENTORY_KPIS = new Set(['critical', 'gap', 'overpriced']);
const LOADING = 'Loading inventory…';

/** Measure groups, in reading order (on a wide screen: age + listings, then risk + pricing, 4 cards a row). */
export const KPI_GROUPS = [
    { title: 'Age of stock', ids: ['aged', 'critical', 'avgDays'] },
    { title: 'Listings', ids: ['daysListed'] },
    { title: 'Risk and demand', ids: ['risk', 'active'] },
    { title: 'Pricing', ids: ['gap', 'overpriced'] },
];

const SMALL_TIP = `Fewer than ${SMALL_SAMPLE_MIN} units: a single unit moves this share a lot, so treat the difference with care.`;
const VERDICT_TIP = `Better or Worse is judged in the direction that is good for the business for each measure (for example, fewer old units is better). `
    + `A difference under ${SIMILAR_BAND.rate.toFixed(1)} pt for shares and asking vs AI, or under ${SIMILAR_BAND.days} days for day figures, counts as Similar.`;
const TITLE_TIP = 'Compares the selection with the benchmark chosen under "Compare with", on shares and averages, so big and small branches can be compared fairly.';
export const GAPS_TIP = 'The measures where the selection differs most from the benchmark, ranked by the difference as a share of the range across branches '
    + '(lowest to highest branch), so points, days and dollars compare fairly. Without a range, by how many times the difference exceeds its similar band. '
    + 'Similar measures and small samples are left out.';

/** "small sample" tag; a button so its hint is reachable by keyboard. */
export function SmallTag({ tip = SMALL_TIP, label = 'small sample' }) {
    return (
        <Tooltip content={tip}>
            <button type="button" className="br2-cmp-tag">{label}</button>
        </Tooltip>
    );
}

/**
 * A horizontally scrolling table box with a subtle "Scroll for more →" cue
 * and an edge fade while there is more to the right (narrow screens).
 */
export function ScrollBox({ children, testId = 'scroll-box' }) {
    const ref = useRef(null);
    const [more, setMore] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const update = () => setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
        update();
        el.addEventListener('scroll', update, { passive: true });
        let ro = null;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(update);
            ro.observe(el);
            if (el.firstElementChild) ro.observe(el.firstElementChild);
        }
        return () => {
            el.removeEventListener('scroll', update);
            if (ro) ro.disconnect();
        };
    }, []);
    return (
        <div className={`br2-tscrollw${more ? ' has-more' : ''}`} data-testid={testId}>
            <div ref={ref} className="v2-tscroll br2-tscroll">{children}</div>
            {more && <p className="br2-scrollcue" aria-hidden="true">Scroll for more →</p>}
        </div>
    );
}

ScrollBox.propTypes = { children: PropTypes.node.isRequired, testId: PropTypes.string };

/** Category breakdown: the selection against the benchmark within each category. */
export function CategoryCompare({
    categories, oldest, benchName, selectionName, single = true, inventoryReady = true, benchNames = [],
}) {
    const [all, setAll] = useState(false);
    if (!inventoryReady) {
        return (
            <div className="br2-cmp-cat">
                <h3 id="br2-cmpcat-h">By category</h3>
                <p className="br2-cnote br2-cnote--in" data-testid="cmp-cat-loading">{LOADING}</p>
            </div>
        );
    }
    if (!categories.length) return null;
    const folds = categories.length > CATEGORY_COMPARE_FOLD;
    const shown = folds && !all ? categories.slice(0, CATEGORY_COMPARE_FOLD) : categories;
    // Short, stable column headers; the full names are in the key above.
    const selCol = single ? 'This branch' : 'Selection';
    // A count ("4 branches") names its branches on hover.
    const benchTitle = chosenNamesTip(benchNames) || benchName;
    return (
        <div className="br2-cmp-cat">
            <div className="br2-row">
                <h3 id="br2-cmpcat-h">By category</h3>
                <span className="br2-sub">{categories.length} {categories.length === 1 ? 'category' : 'categories'} in the selection</span>
            </div>
            <p className="br2-cnote br2-cnote--in br2-cmp-mix" data-testid="mix-note">
                Each equipment category is compared on its own, so a different equipment mix cannot make a branch look better or worse.
                <Tooltip
                    label="About comparing by category"
                    content="A branch that stocks more slow-selling categories can look worse overall and still match the benchmark category by category. Then the difference comes from what it stocks, not how it is managed."
                />
            </p>
            <p className="br2-cmp-catkey" data-testid="cmp-cat-key">
                <span><b>{selCol}:</b> {selectionName}</span>
                <span aria-hidden="true"> · </span>
                <span><b>Benchmark:</b> <BenchLabel text={benchName} names={benchNames} /></span>
            </p>
            <ScrollBox testId="cmp-cat-scroll">
                <table className="v2-table br2-cmpt br2-cmpt--cat" aria-labelledby="br2-cmpcat-h">
                    <colgroup>
                        <col className="br2-cc-name" />
                        <col className="br2-cc-units" />
                        <col className="br2-cc-v" /><col className="br2-cc-v" /><col className="br2-cc-vd" />
                        <col className="br2-cc-v" /><col className="br2-cc-v" /><col className="br2-cc-vd" />
                    </colgroup>
                    <thead>
                        <tr className="br2-cmp-hg">
                            <td colSpan={2} />
                            <th scope="colgroup" colSpan={3} className="br2-cmp-hgc">
                                <span className="br2-th-i">
                                    Older than {oldest} days
                                    <Tooltip
                                        label={`About Older than ${oldest} days by category`}
                                        content={`Share of the category's units in stock longer than ${oldest} days. Lower is better.`}
                                    />
                                </span>
                            </th>
                            <th scope="colgroup" colSpan={3} className="br2-cmp-hgc">
                                <span className="br2-th-i">
                                    High sell-speed risk
                                    <Tooltip
                                        label="About High sell-speed risk by category"
                                        content="Share of the category's scored units the AI expects to sell slowly. Lower is better."
                                    />
                                </span>
                            </th>
                        </tr>
                        <tr>
                            <th scope="col">Category</th>
                            <th scope="col" className="v2-r">Units</th>
                            <th scope="col" className="v2-r br2-cmp-hgs" title={selectionName}>{selCol}</th>
                            <th scope="col" className="v2-r" title={benchTitle}>Benchmark</th>
                            <th scope="col">Verdict</th>
                            <th scope="col" className="v2-r br2-cmp-hgs" title={selectionName}>{selCol}</th>
                            <th scope="col" className="v2-r" title={benchTitle}>Benchmark</th>
                            <th scope="col">Verdict</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((c) => (
                            <tr key={c.name} className={c.small ? 'br2-cmp-small' : undefined} data-testid="cmp-cat-row" data-cat={c.name}>
                                <th scope="row">
                                    <span className="br2-cmp-k">{c.name}</span>
                                    {c.small && <SmallTag />}
                                </th>
                                <td className="v2-r br2-num">{n(c.units)}</td>
                                <td className="v2-r br2-num br2-cmp-hgs br2-cmp-v">{formatKpiValue('rate', c.agedSel)}</td>
                                <td className="v2-r br2-num br2-ink-muted">{formatKpiValue('rate', c.agedBench)}</td>
                                <td><CompareDelta delta={c.agedDelta} testId="cmp-cat-verdict" /></td>
                                <td className="v2-r br2-num br2-cmp-hgs br2-cmp-v">{formatKpiValue('rate', c.riskSel)}</td>
                                <td className="v2-r br2-num br2-ink-muted">{formatKpiValue('rate', c.riskBench)}</td>
                                <td><CompareDelta delta={c.riskDelta} testId="cmp-cat-verdict" /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </ScrollBox>
            {folds && (
                <button type="button" className="v2-clear br2-fold" aria-expanded={all} onClick={() => setAll((v) => !v)}>
                    {all ? 'Show fewer compared categories' : `Show all ${categories.length} compared categories`}
                </button>
            )}
        </div>
    );
}

CategoryCompare.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.object).isRequired,
    oldest: PropTypes.number.isRequired,
    benchName: PropTypes.string.isRequired,
    selectionName: PropTypes.string.isRequired,
    single: PropTypes.bool,
    inventoryReady: PropTypes.bool,
    benchNames: PropTypes.arrayOf(PropTypes.string),
};

const BENCH_HOW = {
    dealership: 'Whole dealership adds up every branch, this selection included, and then works out each share. Big branches therefore count for more, exactly as in the dealership totals elsewhere in IronSight.',
    others: 'Other branches adds up every branch except the selection and then works out each share, so the selection is not compared with itself.',
    median: 'Median branch ranks every branch on each measure and takes the one in the middle, so one unusual branch cannot pull the benchmark.',
};

/**
 * How the chosen branches make the benchmark: one is compared as it is, several
 * are added up like Other branches. `what` names the figures ("shares and
 * averages", "sales and stock"). Shared with SalesPerformance.
 */
export const chosenHow = (names, what) => {
    const who = benchmarkWho('chosen', 0, names);
    if (names.length === 1) return `${who} is compared directly with the selection: its own ${what}, nothing added up.`;
    return `${who} are added up together, as for Other branches, before working out each figure, so a bigger branch among them counts for more.`;
};

/** "How to read this": the plain-language guide, closed by default. */
function HowToRead({ basis, chosenNames = [] }) {
    return (
        <details className="br2-cmp-how" data-testid="cmp-howto">
            <summary>How to read this</summary>
            <ul>
                <li>The panel compares shares and averages, not counts, so a small branch can be compared fairly with a big one.</li>
                <li>{basis === 'chosen' ? chosenHow(chosenNames, 'shares and averages') : BENCH_HOW[basis]}</li>
                <li>
                    On each card the shaded track runs from the lowest to the highest branch. The dot is your selection; the dark line is the benchmark. The chip says whether the
                    difference is good or bad for that measure. When every branch has the same figure, the card says so instead of drawing a track.
                </li>
                <li>
                    A difference under {SIMILAR_BAND.rate.toFixed(1)} point for shares and asking vs AI, or under {SIMILAR_BAND.days} days for day figures,
                    counts as Similar.
                </li>
                <li>
                    Figures over fewer than {SMALL_SAMPLE_MIN} units are greyed out and marked &quot;small sample&quot;: one unit can move them a lot. They
                    are left out of the summary at the top.
                </li>
                <li>
                    &quot;By category&quot; compares each equipment category on its own. If the selection looks worse overall but similar category by
                    category, the difference comes from what it stocks, not from how it is managed.
                </li>
            </ul>
        </details>
    );
}

HowToRead.propTypes = { basis: PropTypes.string.isRequired, chosenNames: PropTypes.arrayOf(PropTypes.string) };

SmallTag.propTypes = { tip: PropTypes.node, label: PropTypes.string };

/** Benchmark wording under a figure: "dealership 1,189" / "median of 4 branches" (or the row's own `nText`). */
const nLine = (r, benchShort) => r.nText || (r.benchNoun === 'branches'
    ? `${n(r.selN)} ${r.nNoun} · median of ${n(r.benchN)} branches`
    : `${n(r.selN)} ${r.nNoun} · ${benchShort} ${n(r.benchN)}`);

/** A figure with a tooltip of its own (hover and keyboard focus); the figure alone without `tip`. */
function FigTip({ tip = null, children }) {
    if (!tip) return children;
    return (
        <Tooltip content={tip}>
            {React.cloneElement(children, { tabIndex: 0, className: [children.props.className, 'br2-figtip'].filter(Boolean).join(' ') })}
        </Tooltip>
    );
}

FigTip.propTypes = { tip: PropTypes.string, children: PropTypes.element.isRequired };

/**
 * A status chip in place of the verdict (months of supply): `status` →
 * { word, tone } via `statuses`; the row's `statusText` reads "Overstocked:
 * 9.1 months vs 4.2 dealership", the chip carries the part before the colon.
 */
function StatusLine({ r, statuses }) {
    const st = statuses[r.status];
    const text = r.statusText || '';
    const rest = st && text.startsWith(`${st.word}: `) ? text.slice(st.word.length + 2) : text;
    return (
        <span className="br2-vd" data-testid="cmp-status" data-status={r.status || 'none'}>
            {st && <Chip tone={st.tone} className="br2-vd-chip">{st.word}</Chip>}
            <span className="br2-vd-t">{rest}</span>
        </span>
    );
}

StatusLine.propTypes = { r: PropTypes.object.isRequired, statuses: PropTypes.object.isRequired };

/**
 * One measure: label + hint, verdict chip, the big selection figure, the
 * benchmark beside it, the track, the difference. Optional row fields:
 * `selText` / `benchText` (a word instead of the figure), `note` (a plain
 * line under the figure), `status` + `statusText` (a status chip instead of
 * the verdict, with `statuses`), `nText` (the foot line), `selTip` /
 * `benchTip` (a tooltip on the figure, which then takes keyboard focus).
 * `children` follow at the foot of the card.
 */
export function MeasureCard({
    r, benchShort, loading, single, loadingLabel = LOADING, statuses = null, smallTip = SMALL_TIP, children = null, benchNames = [],
}) {
    const benchStatus = statuses && r.benchStatus ? statuses[r.benchStatus] : null;
    return (
        <li className={['br2-mc', !loading && r.small ? 'br2-cmp-small' : null].filter(Boolean).join(' ')} data-kpi={r.id}>
            <div className="br2-mc-h" data-testid="cmp-measure">
                <span className="br2-cmp-k">{r.label}</span>
                {r.hint && <Tooltip label={`About ${r.label}`} content={r.hint} />}
            </div>
            {loading ? (
                <p className="br2-mc-load">{loadingLabel}</p>
            ) : (
                <>
                    <div className="br2-mc-fig">
                        <FigTip tip={r.selTip}>
                            <span className="br2-mc-n" data-testid="cmp-sel">{r.selText || formatKpiValue(r.kind, r.sel)}</span>
                        </FigTip>
                        <span className="br2-mc-vs">
                            vs <FigTip tip={r.benchTip}><b data-testid="cmp-bench">{r.benchText || formatKpiValue(r.kind, r.bench)}</b></FigTip>{' '}
                            <BenchLabel text={benchShort} names={benchNames} />
                            {benchStatus && <span className="br2-mc-bst" data-testid="cmp-bench-status"> · {benchStatus.word}</span>}
                        </span>
                        {r.note && <span className="br2-mc-note" data-testid="cmp-note">{r.note}</span>}
                    </div>
                    <BulletBar sel={r.sel} bench={r.bench} lo={r.rangeLo} hi={r.rangeHi} kind={r.kind} single={single} />
                    <div className="br2-mc-f">
                        {statuses && r.statusText != null && <StatusLine r={r} statuses={statuses} />}
                        {!statuses && (r.verdict ? <CompareDelta delta={r} /> : <span className="br2-mc-d">No figure to compare</span>)}
                        {statuses && r.statusText == null && <span className="br2-mc-d">No figure to compare</span>}
                        {r.small && <SmallTag tip={smallTip} />}
                    </div>
                    <span className="br2-mc-nl" data-testid="cmp-n">{nLine(r, benchShort)}</span>
                </>
            )}
            {children}
        </li>
    );
}

MeasureCard.propTypes = {
    r: PropTypes.object.isRequired,
    benchShort: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    single: PropTypes.bool,
    loadingLabel: PropTypes.string,
    statuses: PropTypes.object,
    smallTip: PropTypes.string,
    children: PropTypes.node,
    benchNames: PropTypes.arrayOf(PropTypes.string),
};

export const TALLY = [
    { key: 'worse', label: 'worse', tone: 'red' },
    { key: 'better', label: 'better', tone: 'sage' },
    { key: 'similar', label: 'similar', tone: 'grey' },
];

/**
 * The hero: the tally (worse / better / similar), the plain-English summary
 * and the biggest gaps. `children` adds a line under them (a loading note).
 */
export function CompareHero({
    takeaway, children = null, gapsTip = GAPS_TIP, testId = 'cmp-takeaway',
}) {
    return (
        <div className="br2-cmp-hero" data-testid={testId}>
            <ul className="br2-tally" aria-label="Measures compared">
                {TALLY.map((t) => (
                    <li key={t.key} className="br2-tally-i" data-testid={`cmp-tally-${t.key}`}>
                        <span className="br2-tally-n">{takeaway.counts[t.key]}</span>
                        <span className="v2-bk-l"><i className={`v2-bk-dot v2-tone-${t.tone}`} aria-hidden="true" />{t.label}</span>
                    </li>
                ))}
            </ul>
            <div className="br2-cmp-story">
                <p className="br2-cmp-sum" data-testid="cmp-summary">{takeaway.summary}</p>
                {takeaway.gaps.length > 0 && (
                    <div className="br2-cmp-gaps">
                        <span className="br2-cmp-gapsh">
                            <span className="v2-eyebrow">Biggest gaps</span>
                            <Tooltip label="About Biggest gaps" content={gapsTip} />
                        </span>
                        <ul>
                            {takeaway.gaps.map((g) => (
                                <li key={g.id} data-testid="cmp-gap">
                                    <Chip tone={VERDICT[g.verdict].tone} className="br2-vd-chip">{VERDICT[g.verdict].word}</Chip>
                                    <span>{g.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {children}
            </div>
        </div>
    );
}

CompareHero.propTypes = {
    takeaway: PropTypes.shape({ summary: PropTypes.string, gaps: PropTypes.array, counts: PropTypes.object }).isRequired,
    children: PropTypes.node,
    gapsTip: PropTypes.string,
    testId: PropTypes.string,
};

/** The key above the cards: the legend of the track and the verdict hint. */
export function CompareKey({ selectionName, benchName, verdictTip, extra = null }) {
    return (
        <div className="br2-cmp-key">
            <span className="br2-cmp-legend" data-testid="cmp-legend" aria-hidden="true">
                <span className="br2-lg"><span className="br2-lg-sel" />{selectionName}</span>
                <span className="br2-lg"><span className="br2-lg-bench" />{benchName}</span>
                <span className="br2-lg"><span className="br2-lg-rng" />Range across all branches</span>
            </span>
            <span className="br2-cmp-vk">
                How verdicts are decided
                <Tooltip label="About the verdict" content={verdictTip} />
                {extra}
            </span>
        </div>
    );
}

CompareKey.propTypes = {
    selectionName: PropTypes.string.isRequired, benchName: PropTypes.string.isRequired, verdictTip: PropTypes.string.isRequired, extra: PropTypes.node,
};

/**
 * "Branch vs dealership": a hero with the tally (worse / better / similar),
 * the plain-English takeaway and the biggest gaps; then one card per KPI,
 * grouped (age of stock, listings, risk and demand, pricing), each with the
 * selection's figure, the benchmark, a comparison track and a verdict chip
 * with the plain difference. A selection figure over fewer than 5 units is
 * muted and labelled "small sample". Below: the category breakdown (mix
 * effect) and "How to read this".
 */
export default function ComparePanel({
    rows, categories, selectionName, selectedNames, benchName, benchLabel, benchShort, basis, otherCount, oldest,
    criticalDays, inventoryReady = true, chosenNames = [],
}) {
    const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
    const isLoading = (r) => !inventoryReady && INVENTORY_KPIS.has(r.id);
    const takeaway = useMemo(() => {
        const { subject, plural } = selectionSubject(selectedNames);
        return compareTakeaway(rows.filter((r) => !isLoading(r)), {
            subject, plural, bench: benchmarkPhrase(basis, otherCount, chosenNames), oldest, criticalDays,
        });
    }, [rows, selectedNames, basis, otherCount, chosenNames, oldest, criticalDays, inventoryReady]); // eslint-disable-line react-hooks/exhaustive-deps
    const title = compareTitle(basis, selectedNames.length, chosenNames);
    const single = selectedNames.length === 1;
    const benchNames = basis === 'chosen' ? chosenNames : [];

    return (
        <section className="v2-card br2-cmp" aria-labelledby="br2-vs-h" data-testid="compare-panel">
            <div className="br2-cmp-head">
                <h2 id="br2-vs-h">{title}</h2>
                <Tooltip label={`About ${title}`} content={TITLE_TIP} />
                <span className="br2-sub" data-testid="cmp-basis">
                    {selectionName} <BenchLabel text={benchLabel} names={benchNames} />
                </span>
            </div>

            <CompareHero takeaway={takeaway}>
                {!inventoryReady && <p className="br2-sub">Pricing and critical-age figures follow once the inventory has loaded.</p>}
            </CompareHero>

            <CompareKey selectionName={selectionName} benchName={benchName} verdictTip={VERDICT_TIP} />

            <div className="br2-mgrid" aria-label="Measures">
                {KPI_GROUPS.map((g) => {
                    const groupRows = g.ids.map((id) => byId.get(id)).filter(Boolean);
                    if (!groupRows.length) return null;
                    return (
                        <section key={g.title} className={`br2-mgroup br2-mgroup--${groupRows.length}`} aria-label={g.title}>
                            <h3 className="v2-kpi-title" data-testid="cmp-group">{g.title}</h3>
                            <ul className="br2-mcards">
                                {groupRows.map((r) => <MeasureCard key={r.id} r={r} benchShort={benchShort} benchNames={benchNames} loading={isLoading(r)} single={single} />)}
                            </ul>
                        </section>
                    );
                })}
            </div>

            <div className="br2-cmp-catw">
                <CategoryCompare
                    categories={categories}
                    oldest={oldest}
                    benchName={benchName}
                    selectionName={selectionName}
                    single={single}
                    inventoryReady={inventoryReady}
                    benchNames={benchNames}
                />
            </div>
            <HowToRead basis={basis} chosenNames={chosenNames} />
        </section>
    );
}

ComparePanel.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    categories: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectionName: PropTypes.string.isRequired,
    selectedNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    benchName: PropTypes.string.isRequired,
    benchLabel: PropTypes.string.isRequired,
    benchShort: PropTypes.string.isRequired,
    basis: PropTypes.string.isRequired,
    otherCount: PropTypes.number.isRequired,
    oldest: PropTypes.number.isRequired,
    criticalDays: PropTypes.number,
    inventoryReady: PropTypes.bool,
    chosenNames: PropTypes.arrayOf(PropTypes.string),
};
